import { defineStore } from "pinia";
import equal from "fast-deep-equal";

import type { Account, Rule, PlatformDetails, PlatformAccount, Signal, PublishSummary } from "@/Connect/types";

import { deepClone, generateTemplateLabel } from "@/utils";

interface Option {
    label: string;
    value: string;
}

export interface EditableGroupOption {
    name: string;
    editableOptions: Option[];
}

export type EditableGroupOptions = Record<string, EditableGroupOption>;

function accountSort(a: Account, b: Account) {
    if (a.platformAccount.status === "active" && b.platformAccount.status === "deleted") {
        return -1;
    }

    if (a.platformAccount.status === "deleted" && b.platformAccount.status === "active") {
        return 1;
    }

    return a.platformAccount.order - b.platformAccount.order;
}

function ruleSort(a: Rule, b: Rule) {
    if (a.status !== "deleted" && b.status === "deleted") {
        return -1;
    }

    if (a.status === "deleted" && b.status !== "deleted") {
        return 1;
    }

    return a.order - b.order;
}

interface State {
    showDeletedAccounts: boolean;
    accounts: Account[] | null;
    latestVersionId: string;
    savedAccounts: Account[];
    publishSummary: Record<string, PublishSummary | undefined>;

    totalVariations: number;

    languageOptions: Option[];
    sizeOptions: Option[];
    editableGroupOptions: EditableGroupOptions;
}

export const useConnectStore = defineStore("connect", {
    state: (): State => ({
        showDeletedAccounts: false,
        accounts: null,
        latestVersionId: "",
        savedAccounts: [],
        publishSummary: {},

        languageOptions: [],
        sizeOptions: [],
        editableGroupOptions: {},

        totalVariations: 0
    }),

    getters: {
        hasFilterOptionsForEachType(state) {
            return !!(
                state.languageOptions.length &&
                state.sizeOptions.length &&
                Object.keys(state.editableGroupOptions).length
            );
        },

        getAccountByName(state) {
            return (name: string) => {
                return state.accounts?.find(account => account.platformAccount.name === name);
            };
        },

        getRuleByName(state) {
            return (accountName: string, ruleName: string) => {
                const account = state.accounts?.find(account => account.platformAccount.name === accountName);

                if (!account) {
                    return;
                }

                return account.rules.find(rule => rule.name === ruleName);
            };
        },

        hasAccountChanges(state) {
            return !equal(state.savedAccounts, state.accounts);
        },

        visibleAccounts(state) {
            if (state.showDeletedAccounts) {
                return state.accounts;
            }

            return state.accounts
                ?.filter(account => account.platformAccount.status === "active")
                .map(account => {
                    const filteredRules = account.rules.filter(rule => rule.status !== "deleted");

                    return {
                        ...account,
                        rules: filteredRules
                    };
                });
        },

        visibleAccountsWithActiveRules(): Account[] | undefined {
            const visible = this.visibleAccounts;
            if (!Array.isArray(visible)) return undefined;
            return visible.filter(account => account.rules.some(rule => rule.status === "active"));
        },

        hasInProgressStatus() {
            return false;
        },

        hasActiveAccounts(state) {
            if (state.showDeletedAccounts) {
                return state.accounts?.length;
            }

            return state.accounts?.some(account => account.platformAccount.status !== "deleted");
        },

        hasActiveRules(state) {
            if (!Array.isArray(state.accounts)) return false;

            return state.accounts.some(
                account =>
                    account.platformAccount.status === "active" && account.rules.some(rule => rule.status === "active")
            );
        },

        getSelectedNumberOfVariants() {
            return (accountName: string, ruleName: string) => {
                const rule = this.getRuleByName(accountName, ruleName);

                if (!rule?.variantsFilters) {
                    return 0;
                }

                const { variantsFilters } = rule;

                let result = 1;

                result *= variantsFilters.languages.length;
                result *= variantsFilters.masterTemplateIds.length;

                result *= Object.values(this.editableGroupOptions).reduce((acc, group) => {
                    const count = group.editableOptions.filter(option =>
                        variantsFilters.editableGroupValueIds.includes(option.value)
                    ).length;

                    return acc * count;
                }, 1);

                return result;
            };
        }
    },

    actions: {
        setSizeOptionsFromMasterTemplates(masterTemplates: { height: number; width: number; _id: string }[]) {
            const sortedMasterTemplates = masterTemplates.toSorted((a, b) => {
                if (a.width === b.width) {
                    return a.height - b.height;
                }

                return a.width - b.width;
            });

            const sizeLabels = [] as string[];

            this.sizeOptions = sortedMasterTemplates.map(template => {
                const label = generateTemplateLabel(template, sizeLabels);
                sizeLabels.push(label);

                return {
                    label,
                    value: template._id
                };
            });
        },

        setEditableGroupOptionsFromGroupValues(
            groupValues: { editableGroupName: string; value: string; _id: string }[]
        ) {
            this.editableGroupOptions = groupValues.reduce<EditableGroupOptions>((acc, group) => {
                acc[group.editableGroupName] ??= {
                    name: group.editableGroupName,
                    editableOptions: []
                };

                acc[group.editableGroupName]?.editableOptions.push({
                    label: group.value,
                    value: group._id
                });

                return acc;
            }, {});
        },

        setPublishSummary(apiPublishStatus: PublishSummary[]) {
            this.publishSummary = apiPublishStatus.reduce<Record<string, PublishSummary>>((acc, item) => {
                acc[item.accountId] = item;

                return acc;
            }, {});
        },

        setAccounts(apiAccounts: Account[]) {
            this.accounts = apiAccounts.sort(accountSort).map(account => ({
                ...account,
                rules: account.rules.sort(ruleSort)
            }));

            this.savedAccounts = deepClone(this.accounts);
        },

        resetToSavedAccounts() {
            this.setAccounts(this.savedAccounts);
        },

        /**
         * the indexes sent here come from the visible accounts list,
         * but they map to the actual accounts array since the deleted accounts are always at the end
         * sketchy, but it works
         */
        changeAccountOrder(oldIndex: number, newIndex: number) {
            if (!this.accounts) {
                console.error("No accounts found");

                return;
            }

            const account = this.accounts[oldIndex];

            if (!account) {
                console.error("Account not found at index");

                return;
            }

            this.accounts.splice(oldIndex, 1);
            this.accounts.splice(newIndex, 0, account);

            for (let i = 0; i < this.accounts.length; i++) {
                const currentAccount = this.accounts[i];

                if (currentAccount) {
                    currentAccount.platformAccount.order = i + 1;
                }
            }

            this.accounts.sort(accountSort);
        },

        updateRuleOrder(accountName: string, oldIndex: number, newIndex: number) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("No accounts found");

                return;
            }

            const rule = account.rules[oldIndex];

            if (!rule) {
                console.error("Rule not found at index");

                return;
            }

            account.rules.splice(oldIndex, 1);
            account.rules.splice(newIndex, 0, rule);

            for (let i = 0; i < account.rules.length; i++) {
                const currentRule = account.rules[i];

                if (currentRule) {
                    currentRule.order = i + 1;
                }
            }
        },

        addPlatformAccount({ name, platformDetails }: { name: string; platformDetails: PlatformDetails }) {
            if (!this.accounts) {
                console.error("No accounts found");

                return;
            }

            const maxOrder = this.accounts.reduce((acc, account) => Math.max(acc, account.platformAccount.order), 0);

            const newPlatformAccount = {
                order: maxOrder + 1,
                name,
                status: "active",
                platformDetails,
                lineType: "platformAccount"
            } as PlatformAccount;

            this.accounts.push({ platformAccount: newPlatformAccount, rules: [] });
            this.accounts.sort(accountSort);
        },

        addRule(
            platformAccountName: string,
            { name, adSetId, status }: { name: string; adSetId: string; status: "active" | "draft" }
        ) {
            const account = this.getAccountByName(platformAccountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const maxOrder = account.rules.reduce((acc, rule) => Math.max(acc, rule.order), 0);

            const newRule = {
                order: maxOrder + 1,
                name,
                status,
                signals: [],
                targeting: [
                    {
                        name: "Locations",
                        value: "UK, US, AU"
                    },
                    {
                        name: "Audience",
                        value: "Age 20-30"
                    },
                    {
                        name: "Position",
                        value: "News Feed, ..."
                    }
                ],
                rulePlatformDetails: {
                    adSetId
                }
            };

            account.rules.push(newRule);
            account.rules.sort(ruleSort);
        },

        updateRuleSignals(accountName: string, ruleName: string, newSignals: Signal[]) {
            const rule = this.getRuleByName(accountName, ruleName);

            if (!rule) {
                console.error("Rule not found");

                return;
            }

            rule.signals = newSignals;
            rule.isRuleActiveBySignals = false;
        },

        updatePlatformAccount(
            accountName: string,
            { name, platformDetails }: { name: string; platformDetails: Partial<PlatformDetails> }
        ) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            account.platformAccount.name = name;
            account.platformAccount.platformDetails = {
                ...account.platformAccount.platformDetails,
                ...platformDetails
            };
        },

        updateRule(
            accountName: string,
            ruleName: string,
            { name, adSetId, status }: { name: string; adSetId: string; status: "active" | "draft" }
        ) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const rule = this.getRuleByName(accountName, ruleName);

            if (!rule) {
                console.error("Rule not found");

                return;
            }

            rule.name = name;
            rule.status = status;
            rule.rulePlatformDetails = { adSetId };

            account.rules.sort(ruleSort);
        },

        duplicateRule(accountName: string, rule: Rule) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const duplicatedRule = deepClone(rule);

            const maxOrder = account.rules.reduce((acc, rule) => Math.max(acc, rule.order), 0);

            duplicatedRule.order = maxOrder + 1;
            duplicatedRule.name = `${rule.name} - Duplicated`;
            duplicatedRule.status = "draft";

            delete duplicatedRule.id;

            account.rules.push(duplicatedRule);
            account.rules.sort(ruleSort);
        },

        deletePlatformAccount(accountName: string) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            if (!this.accounts) {
                console.error("Accounts not found");

                return;
            }

            account.platformAccount.status = "deleted";
            this.accounts.sort(accountSort);
        },

        deleteRule(accountName: string, ruleName: string) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const rule = this.getRuleByName(accountName, ruleName);

            if (!rule) {
                console.error("Rule not found");

                return;
            }

            rule.status = "deleted";
            account.rules.sort(ruleSort);
        },

        restoreDeletedPlatformAccount(accountName: string) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            if (!this.accounts) {
                console.error("Accounts not found");

                return;
            }

            account.platformAccount.status = "active";
            this.accounts.sort(accountSort);
        },

        restoreRule(accountName: string, ruleName: string) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const rule = this.getRuleByName(accountName, ruleName);

            if (!rule) {
                console.error("Rule not found");

                return;
            }

            rule.status = "active";
            account.rules.sort(ruleSort);
        },

        setVariantFilters(accountName: string, ruleName: string, filters: Rule["variantsFilters"]) {
            const account = this.getAccountByName(accountName);

            if (!account) {
                console.error("Account not found");

                return;
            }

            const rule = this.getRuleByName(accountName, ruleName);

            if (!rule) {
                console.error("Rule not found");

                return;
            }

            rule.variantsFilters = filters;
        }
    }
});
